import { call, put, select } from 'redux-saga/effects'
import { ApiResponse } from 'apisauce'

import { FullRequestHotelsHistory, ResumeHotel } from 'src/types'
import api from 'src/services/api'

import { ApplicationState } from 'src/core/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'
import findBasicApiResponseErrors from 'src/services/findBasicApiResponseErrors'
import i18n from 'src/config/i18n'

import objectToQueryString from 'src/helpers/object-to-query-string'
import * as Actions from './actions'

const axiosConfig = {
  timeout: 72000 // 1m20min
}

export function* getHotels({
  payload: { customer_supplier_id, filters, reason_id, searchKey }
}: ReturnType<typeof Actions.getHotels>) {
  try {
    const { user } = yield select((state: ApplicationState) => state.me)

    const queryString = objectToQueryString({
      bedrooms: filters.bedrooms,
      checkin_date: filters.checkin_date,
      checkout_date: filters.checkout_date,
      customer_supplier_id,
      num_adults: filters.num_adults,
      reason_id,
      resume: 'True',
      trend_destination_id: filters.trend_destination_id
    })

    const response: ApiResponse<{
      resume_hotel: ResumeHotel[]
      message_id: string[]
    }> = yield call(
      api.get,
      `organizations/${user.organization_id}/hotels${queryString}`,
      null,
      axiosConfig
    )

    if (response.ok) {
      yield put(
        Actions.getHotelsSuccess(
          searchKey,
          response.data.resume_hotel,
          response.data.message_id
        )
      )
      return
    }

    if (response.status === 404) {
      // @ts-ignore
      if (response?.data?.exception === 'NotFound') {
        yield put(Actions.getHotelsSuccess(searchKey, [], []))
        return
      }
    }

    if (response.status === 500) {
      // @ts-ignore
      const reason = response.data?.reasons?.error || response.data?.reasons
      if (typeof reason === 'string' && reason) {
        yield put(Actions.getHotelsFailure(searchKey, reason))
        return
      }
    }

    const message = findBasicApiResponseErrors(response)
    yield put(Actions.getHotelsFailure(searchKey, message))
  } catch (err) {
    ClientNotifyCatch('Saga getHotels', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(Actions.getHotelsFailure(searchKey, message))
  }
}

export function* getHotelsHistory({
  payload: { request_id }
}: ReturnType<typeof Actions.getHotelsHistory>): unknown {
  try {
    const { user } = yield select((state: ApplicationState) => state.me)

    const response: ApiResponse<FullRequestHotelsHistory> = yield call(
      api.get,
      `organizations/${user.organization_id}/requests/${request_id}/hotel_historic`
    )

    if (response.ok) {
      yield put(
        Actions.getHotelsHistorySuccess({
          historyList: response.data
        })
      )
      return
    }
    const message = findBasicApiResponseErrors(response)
    yield put(Actions.getHotelsHistoryFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga getHotelsHistory', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(Actions.getHotelsHistoryFailure(message))
  }
}
